import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { gsap } from 'gsap';
import { Subscription } from 'rxjs';
import { AppService } from '../../../app.service';
import { ThemeService } from '../../../shared/theme/services/theme.service';
import { AuthMethodModel } from '../../models/auth-method.model';

import { AuthService } from '../../services/auth.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
})
export class LoginComponent implements OnInit, OnDestroy {

  public authenticated: boolean = false;
  public appApi: string = null;
  public allowedAuthMethods: AuthMethodModel[] = [];
  public theme: string = null;
  public showActivationCodeInput: boolean = false;
  public loginIntroTxtEnabled: boolean = false;

  @ViewChild('loginPageElm')
  private set loginElementRef(element: ElementRef) {
    if (element && this.loginFormElement === null) {
      this.loginFormElement = element;

      this.authTL = gsap.timeline({
        paused: true,
        onReverseComplete: () => {
          this.ngZone.run(() => {
            this.router.navigate([this.authService.redirectUrl || '/course']).then(() => {
              this.subscription.unsubscribe();
            });
          });
        },
      });
      this.authTL.fromTo(
        this.loginFormElement.nativeElement,
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.75,
        },
      );

      this.authTL.shiftChildren(0.05);
      this.authTL.play();
    }
  }

  private subscription: Subscription = new Subscription();
  private loginFormElement: ElementRef = null;
  private authTL: gsap.core.Timeline;

  /**
   * The constructor.
   * @param http HttpClient
   * @param route ActivatedRoute
   * @param router Router
   * @param appService
   * @param authService AuthService
   * @param ngZone
   * @param meta
   * @param themeService
   * @param translateService TranslateService
   */
  public constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private appService: AppService,
    private authService: AuthService,
    private ngZone: NgZone,
    private meta: Meta,
    private themeService: ThemeService,
    private translateService: TranslateService,
  ) {
  }

  /**
   * Initialisation
   */
  public ngOnInit() {
    this.appApi = this.meta.getTag('name=application-api').content;

    this.theme = this.themeService.theme;

    this.subscription.add(
      this.authService.isAuthenticatedSubject.subscribe(
        (isAuthenticated: boolean) => {
          if (isAuthenticated) {
            this.themeService.langTheme = null;
            if (this.loginFormElement) {
              this.authTL.reverse();
            }
          }
        },
      ),
    );

    this.authService.getAllowedAuthMethods().subscribe(
      (auth_methods: AuthMethodModel[]) => {
        this.allowedAuthMethods = auth_methods;
      },
    );

    this.translateService.get('auth.labels.login_intro').subscribe(
      (value) => {
        this.loginIntroTxtEnabled = value != '' &&  value != null  && value != '&nbsp;'
      }
    )

  }

  /**
   * Toggle the Activation Code Form
   *
   * @return void
   */
  public toggleActivationCodeForm(): void {
    this.showActivationCodeInput = !this.showActivationCodeInput;
  }

  /**
   * Gets the correct href for the identity providers based on their used method
   *
   * @param {AuthMethodModel} authMethod
   *
   * @return {string}
   */
  public getHrefForAuthMethod(authMethod: AuthMethodModel): string|null {
    switch (authMethod.method) {
      case 'oauth2':
        return this.appApi + '/api/oauth2/redirect/' + authMethod.idp;
      case 'saml2':
        return '/saml2/' + authMethod.idp + '/login';
      default:
        return null;
    }
  }

  /**
   * Check if the form login is enabled
   *
   * @return {boolean}
   */
  public hasFormLogin(): boolean {
    return this.allowedAuthMethods.findIndex((authMethod: AuthMethodModel) => {
      return authMethod.method === 'form';
    }) !== -1;
  }

  /**
   * When the component is destroyed
   *
   * @return void
   */
  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
